<template>
  <!-- Explicit duration keeps the enter classes alive past the fold transition so the slower
       sparkline draw-in (which Vue's auto-detection on the root can't see) finishes uncut. -->
  <Transition name="fold" :duration="{ enter: 800, leave: 200 }">
    <div v-if="visible" class="fold">
      <div class="fold__clip">
        <button type="button" class="preview" aria-label="Open Market insights" @click="emit('open')">
          <span class="preview__title">
            <Text variant="h5" as="span">Market insights</Text>
            <Transition name="badge-fade">
              <Badge
                v-if="positionLabel"
                :label="positionLabel"
                :variant="positionVariant"
                size="small"
                class="preview__badge"
              />
            </Transition>
          </span>

          <!-- Full width on a phone: the viewBox grows with the box, the SVG scales to it -->
          <Sparkline
            :values="sparkValues"
            :width="isMobile ? sparkMobileWidth : undefined"
            :height="isMobile ? sparkMobileHeight : undefined"
            class="preview__spark"
          />

          <span class="preview__body">
            <Text v-if="subtitle" variant="body-small" as="span" color="secondary">{{ subtitle }}</Text>
            <Text variant="label-small" as="span" color="accent">See how your listing stacks up</Text>
          </span>

          <Icon name="chevron-right" size="small" class="preview__chevron" />
        </button>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { Badge, Icon, screenMobile, Sparkline, Text } from '@tallo/design-system';
import { usePropertyPriceTrendQuery, usePropertyRentEstimateQuery, usePropertyStore } from '@tallo/investor/property';

const emit = defineEmits<{ open: [] }>();

const { data, isPending } = usePropertyPriceTrendQuery();
// The same request the Market insights tab makes, so the widget there opens on the cached estimate
const { data: rentEstimate, isPending: isEstimating } = usePropertyRentEstimateQuery();
const propertyStore = usePropertyStore();

// Within this, the asking rent reads as in line rather than a red "2% above"
const inLineBand = 3;

const averagePoints = computed(() => (data.value?.points ?? []).filter((point) => point.averageRent != null));

// Without at least a line's worth of trend data there is nothing to preview — the Market insights
// tab itself stays reachable through the tab bar, so the fold just stays out of the way.
const visible = computed(() => !isPending.value && averagePoints.value.length >= 2);

const sparkValues = computed(() => averagePoints.value.map((point) => point.averageRent as number));

const latestAvg = computed(() => averagePoints.value.at(-1)?.averageRent ?? null);
const firstAvg = computed(() => averagePoints.value[0]?.averageRent ?? null);
const spanMonths = computed(() => Math.max(averagePoints.value.length - 1, 0));
const changePct = computed(() => {
  if (firstAvg.value == null || latestAvg.value == null || firstAvg.value === 0) return null;
  return Math.round(((latestAvg.value - firstAvg.value) / firstAvg.value) * 100);
});

// One sentence of trend; the pitch to open the tab is its own line
const subtitle = computed(() => {
  if (changePct.value != null && changePct.value !== 0 && spanMonths.value >= 1) {
    const direction = changePct.value > 0 ? 'up' : 'down';
    return `Neighborhood rents are ${direction} ${Math.abs(changePct.value)}% over ${spanMonths.value} months.`;
  }
  return null;
});

const isMobile = screenMobile;
// The phone sparkline's own coordinate space; CSS stretches it to the card
const sparkMobileWidth = 320;
const sparkMobileHeight = 72;

const askingRent = computed(() => propertyStore.property?.leaseConditions?.rent ?? null);

// The asking rent against the unit's own estimate, the number the Market insights tab shows. The ZIP's
// average mixes every size and kind of home, so it stands in only when there is no estimate.
const benchmark = computed(() => {
  const estimatedRent = rentEstimate.value?.rent ?? null;
  if (estimatedRent) return { rent: estimatedRent, name: 'estimated rent' };
  if (latestAvg.value) return { rent: latestAvg.value, name: 'market avg' };
  return null;
});

// None until the estimate settles: compared with the ZIP's average first, the badge would jump
const positionPct = computed(() => {
  if (isEstimating.value || askingRent.value == null || !benchmark.value) return null;
  return Math.round(((askingRent.value - benchmark.value.rent) / benchmark.value.rent) * 100);
});

const isInLine = computed(() => positionPct.value != null && Math.abs(positionPct.value) <= inLineBand);

const positionVariant = computed(() => {
  if (positionPct.value == null || isInLine.value) return 'grey';
  return positionPct.value < 0 ? 'green' : 'red';
});

const positionLabel = computed(() => {
  if (positionPct.value == null || !benchmark.value) return null;
  if (isInLine.value) return `In line with ${benchmark.value.name}`;
  return `${Math.abs(positionPct.value)}% ${positionPct.value < 0 ? 'below' : 'above'} ${benchmark.value.name}`;
});
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

// 0fr -> 1fr grid collapse: the fold owns no fixed height, so the card grows smoothly to
// whatever height the content needs. The spacing above lives INSIDE the collapsing box
// (margin-top on .preview) so it animates open with the rest instead of jumping in.
.fold {
  display: grid;
  grid-template-rows: 1fr;
}

.fold__clip {
  overflow: hidden;
  min-height: 0;
}

.preview {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  grid-template-areas:
    'spark title chevron'
    'spark body chevron';
  align-items: center;
  column-gap: var(--t-spacing-md);
  row-gap: var(--t-spacing-xs);
  width: 100%;
  margin-top: var(--t-spacing-lg);
  padding: var(--t-spacing-md);
  text-align: left;
  background: var(--t-color-neutral-white);
  border: 1px solid var(--t-color-neutral-200);
  border-radius: var(--t-border-radius-md);
  cursor: pointer;
  transition:
    box-shadow 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    box-shadow: var(--t-shadow-sm);
    border-color: var(--t-color-neutral-300);

    .preview__chevron {
      color: var(--t-text-color-primary);
    }
  }

  // Stacked on a phone: the title row with the chevron, the chart across, the words under it
  @include mobile {
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas:
      'title chevron'
      'spark spark'
      'body body';
    row-gap: var(--t-spacing-sm);
  }
}

.preview__spark {
  grid-area: spark;

  @include mobile {
    width: 100%;
    height: auto;
  }
}

// The trend sentence and the pitch read as one line where there is room for them; only the
// phone, where there is not, puts the pitch on its own line.
.preview__body {
  grid-area: body;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: var(--t-spacing-xs);
  row-gap: 0;
  min-width: 0;

  @include mobile {
    flex-direction: column;
    row-gap: var(--t-spacing-xs);
  }
}

.preview__title {
  grid-area: title;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--t-spacing-sm);
  min-width: 0;
}

.preview__badge {
  white-space: nowrap;
}

.preview__chevron {
  grid-area: chevron;
  color: var(--t-text-color-tertiary);
  transition: color 0.15s ease;
}

/* Reveal: the fold expands while the row fades in and rises, then the sparkline draws
   itself left to right and its area/end dot fade in as the line arrives. */
.fold-enter-active {
  transition:
    grid-template-rows 0.3s ease,
    opacity 0.3s ease;

  .preview {
    transition: transform 0.3s ease;
  }

  :deep(.sparkline__line) {
    stroke-dasharray: 1;
    animation: spark-draw 0.55s ease-out 0.15s both;
  }

  :deep(.sparkline__area),
  :deep(.sparkline__dot) {
    animation: spark-fade 0.3s ease 0.5s both;
  }
}

.fold-leave-active {
  transition:
    grid-template-rows 0.2s ease,
    opacity 0.2s ease;
}

.fold-enter-from,
.fold-leave-to {
  grid-template-rows: 0fr;
  opacity: 0;
}

.fold-enter-from .preview {
  transform: translateY(0.5rem);
}

.badge-fade-enter-active {
  transition: opacity 0.2s ease;
}

.badge-fade-enter-from {
  opacity: 0;
}

@keyframes spark-draw {
  from {
    stroke-dashoffset: 1;
  }

  to {
    stroke-dashoffset: 0;
  }
}

// No `to` frame: each element animates back up to its own resting opacity
// (0.12 for the area, 1 for the dot).
@keyframes spark-fade {
  from {
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .fold-enter-active,
  .fold-leave-active,
  .fold-enter-active .preview,
  .badge-fade-enter-active {
    transition: none;
  }

  .fold-enter-active :deep(.sparkline__line),
  .fold-enter-active :deep(.sparkline__area),
  .fold-enter-active :deep(.sparkline__dot) {
    animation: none;
  }

  .fold-enter-from .preview {
    transform: none;
  }
}
</style>
